import { formatPhone } from "@rhema/client";
import { Button, cn, TextField } from "@rhema/ui";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { PhoneField, type PhoneValue, phoneOf } from "../people/PhoneField";
import { deviceRegion } from "../people/useContactSync";
import { AuthFrame, AuthHeading, AuthText, BackLink, ErrorNote } from "./SignInScreen";

/** The phone number form at the top of the first sign-in and sign-up pages. */
export function PhoneStartForm({
  onPhone,
  busy,
  label = "Sign in with your phone number",
}: {
  onPhone: (e164: string) => void;
  busy: boolean;
  /** The form's accessible name. */
  label?: string;
}) {
  const [value, setValue] = useState<PhoneValue>(() => ({ region: deviceRegion(), number: "" }));
  const [invalid, setInvalid] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const phone = phoneOf(value);
    if (!phone) {
      setInvalid("Check the number. It does not look like a phone number for the country you picked.");
      return;
    }
    setInvalid(null);
    onPhone(phone);
  };

  return (
    <form onSubmit={submit} aria-label={label} className="flex flex-col gap-3">
      <PhoneField label="Phone number" value={value} onChange={setValue} disabled={busy} error={invalid} />
      <Button type="submit" size="lg" disabled={busy || !value.number.trim()}>
        {busy ? "Sending the code" : "Continue"}
      </Button>
    </form>
  );
}

/** Seconds left until a new code can be sent, counting down once a second. */
export function useCountdown(seconds: number, sentAt: number) {
  const [now, setNow] = useState(() => Date.now());
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new code restarts the countdown
  useEffect(() => {
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [sentAt]);
  return Math.max(0, Math.ceil((sentAt + seconds * 1000 - now) / 1000));
}

/**
 * Six boxes drawn over one real input, so the phone's code autofill and pasting work as they do in
 * any text field.
 */
function CodeBoxes({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(true);
  // A wrong code leaves the field ready for the next one.
  useEffect(() => {
    if (!disabled) input.current?.focus();
  }, [disabled]);
  return (
    <div className="relative">
      <label htmlFor={id} className="sr-only">
        6-digit code
      </label>
      <div aria-hidden="true" className="grid grid-cols-6 gap-2">
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const current = focused && i === Math.min(value.length, 5);
          return (
            <div
              key={i}
              className={cn(
                "flex h-[60px] items-center justify-center rounded-field border bg-surface font-sans text-2xl font-semibold text-ink",
                current ? "border-blue ring-4 ring-blue/10" : "border-line",
              )}
            >
              {value[i] ?? ""}
            </div>
          );
        })}
      </div>
      <input
        ref={input}
        id={id}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        // biome-ignore lint/a11y/noAutofocus: the code is the only thing on this page
        autoFocus
        disabled={disabled}
        value={value}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        className="absolute inset-0 h-full w-full cursor-text border-0 bg-transparent text-transparent caret-transparent opacity-0"
      />
    </div>
  );
}

export function PhoneCodeStep({
  phone,
  resendAfter,
  sentAt,
  onVerify,
  onResend,
  onChangeNumber,
  busy,
  error,
}: {
  phone: string;
  resendAfter: number;
  /** When the last code was sent, in milliseconds. */
  sentAt: number;
  onVerify: (code: string) => void;
  onResend: () => void;
  onChangeNumber: () => void;
  busy: boolean;
  error: string | null;
}) {
  const [code, setCode] = useState("");
  const tried = useRef("");
  const left = useCountdown(resendAfter, sentAt);

  // The code goes as soon as its sixth digit is in, once for each code typed.
  const change = (next: string) => {
    setCode(next);
    if (next.length === 6 && next !== tried.current && !busy) {
      tried.current = next;
      onVerify(next);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.length === 6) {
      tried.current = code;
      onVerify(code);
    }
  };

  return (
    <AuthFrame busy={busy}>
      <BackLink onClick={onChangeNumber}>Change number</BackLink>
      <AuthHeading>Enter the code</AuthHeading>
      <AuthText>
        We sent a 6-digit code to{" "}
        <span className="font-semibold whitespace-nowrap text-ink">{formatPhone(phone)}</span>.
      </AuthText>
      {error && <ErrorNote>{error}</ErrorNote>}
      <form onSubmit={submit} aria-label="Enter the code" className="flex flex-col">
        <CodeBoxes value={code} onChange={change} disabled={busy} />
        <p className="m-0 mt-3 text-sm text-ink-secondary">
          {busy ? "Checking the code." : "Rhema signs you in as soon as the sixth digit is in."}
        </p>
        <Button variant="outline" size="lg" className="mt-7" disabled={busy || left > 0} onClick={onResend}>
          {left > 0 ? `Send a new code in ${left} s` : "Send a new code"}
        </Button>
      </form>
    </AuthFrame>
  );
}

export function PinStep({
  onPin,
  onForgot,
  onBack,
  busy,
  error,
}: {
  onPin: (pin: string) => void;
  /** Resolves once SBI has been asked to email a reset. */
  onForgot: () => Promise<boolean>;
  onBack: () => void;
  busy: boolean;
  error: string | null;
}) {
  const [pin, setPin] = useState("");
  const [asked, setAsked] = useState(false);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onPin(pin);
  };
  const forgot = async () => {
    if (await onForgot()) setAsked(true);
  };
  return (
    <AuthFrame busy={busy}>
      <BackLink onClick={onBack}>Start again</BackLink>
      <AuthHeading>Enter your PIN</AuthHeading>
      <AuthText>This number has two-step verification. Enter the 6-digit PIN you chose for it.</AuthText>
      {error && <ErrorNote>{error}</ErrorNote>}
      {asked && (
        <AuthText>
          If this account has a recovery email, we sent it a link to reset your PIN. Without a recovery email
          the PIN cannot be reset.
        </AuthText>
      )}
      <form onSubmit={submit} className="flex flex-col gap-3">
        <TextField
          label="PIN"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          minLength={6}
          maxLength={6}
          required
          autoFocus
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
        />
        <Button type="submit" size="lg" disabled={busy || pin.length !== 6}>
          {busy ? "Checking the PIN" : "Continue"}
        </Button>
        <Button variant="ghost" onClick={() => void forgot()} disabled={busy || asked}>
          Forgot your PIN?
        </Button>
      </form>
    </AuthFrame>
  );
}

export function NameStep({
  onName,
  busy,
  error,
}: {
  onName: (name: string) => void;
  busy: boolean;
  error: string | null;
}) {
  const [name, setName] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) onName(name.trim());
  };
  return (
    <AuthFrame busy={busy}>
      <AuthHeading>Your name</AuthHeading>
      <AuthText>People see this name when you message them. You can change it later in Settings.</AuthText>
      {error && <ErrorNote>{error}</ErrorNote>}
      <form onSubmit={submit} className="flex flex-col gap-3">
        <TextField
          label="Your name"
          autoComplete="name"
          maxLength={80}
          required
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button type="submit" size="lg" disabled={busy || !name.trim()}>
          {busy ? "Saving" : "Continue"}
        </Button>
      </form>
    </AuthFrame>
  );
}
